import type { Expense, ExpenseShare, User } from './types'

/**
 * Money is integer paise everywhere. A float split is off by a paise and you
 * will spend an evening of the trip finding it.
 */

export const formatMinor = (minor: number, currency = 'INR'): string =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: minor % 100 === 0 ? 0 : 2,
  }).format(minor / 100)

/**
 * Just the symbol — "₹", "$", "฿" — pulled out of the same formatter that
 * prints the amounts, so the glyph on the button is the one on every row.
 */
export const currencySymbol = (currency = 'INR'): string => {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? '₹'
  } catch {
    // An unknown currency code throws rather than falling back.
    return '₹'
  }
}

export const parseAmount = (text: string): number =>
  Math.round(Number.parseFloat(text || '0') * 100) || 0

/**
 * Split evenly, giving the remainder paise to the earliest participants, so the
 * shares always sum back to the exact total.
 */
export function splitEqually(totalMinor: number, userIds: string[]): Array<{ user_id: string; share_minor: number }> {
  if (!userIds.length) return []
  const base = Math.floor(totalMinor / userIds.length)
  let remainder = totalMinor - base * userIds.length
  return userIds.map((user_id) => {
    const extra = remainder > 0 ? 1 : 0
    remainder -= extra
    return { user_id, share_minor: base + extra }
  })
}

/**
 * Net position per person: what they paid minus what they owed. Always computed,
 * never stored — a late-syncing expense from another phone cannot corrupt a
 * stored total.
 */
export function netPositions(
  expenses: Expense[],
  shares: ExpenseShare[],
  users: User[],
): Map<string, number> {
  const net = new Map(users.map((u) => [u.id, 0]))
  const live = new Set(expenses.filter((e) => !e.deleted_at).map((e) => e.id))

  for (const e of expenses) {
    if (e.deleted_at) continue
    net.set(e.paid_by, (net.get(e.paid_by) ?? 0) + e.amount_minor)
  }
  for (const s of shares) {
    if (!live.has(s.expense_id)) continue
    net.set(s.user_id, (net.get(s.user_id) ?? 0) - s.share_minor)
  }
  return net
}

/** Reduce the net positions to the fewest transfers that settle everyone. */
export function settle(net: Map<string, number>): Array<{ from: string; to: string; amount: number }> {
  const debtors = [...net.entries()].filter(([, v]) => v < -0).map(([id, v]) => ({ id, v: -v }))
  const creditors = [...net.entries()].filter(([, v]) => v > 0).map(([id, v]) => ({ id, v }))
  debtors.sort((a, b) => b.v - a.v)
  creditors.sort((a, b) => b.v - a.v)

  const out: Array<{ from: string; to: string; amount: number }> = []
  let i = 0
  let j = 0
  while (i < debtors.length && j < creditors.length) {
    const amount = Math.min(debtors[i].v, creditors[j].v)
    if (amount > 0) out.push({ from: debtors[i].id, to: creditors[j].id, amount })
    debtors[i].v -= amount
    creditors[j].v -= amount
    if (debtors[i].v <= 0) i++
    if (creditors[j].v <= 0) j++
  }
  return out
}
